import {useCallback, useContext, useLayoutEffect, useRef, useState} from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import axios from 'axios';
import {useTranslation} from 'react-i18next';
import {ContentPage} from '../../../../../components/content-page/content-page';
import GenericHeader from '../../../../../components/generic-header/generic-header';
import FlatInput from '../../../../../components/flat-input/flat-input';
import {LoadingContent} from '../../../../../components/loading-content/loading-content';
import {RelationshipDirection} from '../../../../../backend/enums/relationship-direction';
import {RelationshipType} from '../../../../../backend/enums/relationship-type';
import {AffiliationInfo} from '../../../../../backend/interfaces/affiliation-info';
import {User} from '../../../../../backend/interfaces/user';
import {AuthContext} from '../../../../../contexts/auth/context';
import {UsersRepository} from '../../../../../repositories/users';
import {translations} from '../../../../../i18n/translations';
import InviteCaregiver from './invite-caregiver';
import RemoveRelationship from './remove-relationship';

const PAGE_SIZE = 20;
type Contact = {id: string; name: string; picture?: string; type: RelationshipType};
type LoadError = 'error' | 'denied' | 'unavailable';
const errorType = (error: unknown): LoadError => {
  if (axios.isAxiosError(error) && error.response?.status === 403) return 'denied';
  if (axios.isAxiosError(error) && error.response?.status === 404) return 'unavailable';
  return 'error';
};

export default function ProfileInformalCaregiverOfficeScreen({seniorId}: {seniorId: string}) {
  const {t, i18n} = useTranslation();
  const {state: authState} = useContext(AuthContext);
  const selectedSenior = authState.session?.selectedRelationship?.withUser;
  const isSelectedSenior = selectedSenior?.id === seniorId;
  const affiliations = selectedSenior?.id === seniorId ? selectedSenior.affiliations : undefined;
  const affiliationsLoading =
    authState.isRestoringSession || (!authState.session && !!authState.firebaseUser);
  const [profile, setProfile] = useState<User>();
  const [profileError, setProfileError] = useState<LoadError>();
  const [profileLoading, setProfileLoading] = useState(true);
  const generation = useRef(0);

  const loadProfile = useCallback(async () => {
    const requestGeneration = generation.current;
    setProfileLoading(true);
    setProfileError(undefined);
    setProfile(undefined);
    try {
      const user = await UsersRepository.instance.getById(seniorId);
      if (generation.current === requestGeneration) setProfile(user);
    } catch (error) {
      if (generation.current === requestGeneration) setProfileError(errorType(error));
    } finally {
      if (generation.current === requestGeneration) setProfileLoading(false);
    }
  }, [seniorId]);

  useLayoutEffect(() => {
    void loadProfile();
    return () => {
      generation.current += 1;
    };
  }, [loadProfile]);

  const errorAlert = (error: LoadError, retry: () => void) => (
    <Alert
      severity="error"
      action={
        <Button color="inherit" size="small" onClick={retry}>
          {t(translations.family_profile.retry)}
        </Button>
      }
    >
      {t(translations.family_profile[error])}
    </Alert>
  );
  const fields = profile
    ? [
        [translations.user_form.full_name_input, profile.name],
        [translations.user_form.display_name_input, profile.displayName],
        [
          translations.user_form.gender_input,
          profile.gender ? t(translations.genders[profile.gender]) : undefined,
        ],
        [
          translations.user_form.birthday_input,
          profile.birthday
            ? new Date(profile.birthday).toLocaleDateString(i18n.language.replace('_', '-'), {
                timeZone: 'UTC',
              })
            : undefined,
        ],
        [translations.user_form.profession_description_input, profile.occupationDescription],
        [
          translations.user_form.religion_input,
          profile.religion ? t(translations.religions[profile.religion]) : undefined,
        ],
        [translations.user_form.address_input, profile.address],
      ]
    : [];

  return (
    <ContentPage fullWidth>
      <GenericHeader
        isLoading={false}
        pageTitle={t(translations.family_profile.title)}
        leftColumn={<Box />}
      />
      <Stack spacing={3} sx={{mt: 3}}>
        {profileLoading ? (
          <Card>
            <CardContent sx={{minHeight: 220, display: 'flex', flexDirection: 'column'}}>
              <Stack direction="row" alignItems="center" sx={{minHeight: 36.5}}>
                <Typography variant="h6">{t(translations.family_profile.user_details)}</Typography>
              </Stack>
              <LoadingContent sx={{flex: 1}} />
            </CardContent>
          </Card>
        ) : profileError ? (
          errorAlert(profileError, () => void loadProfile())
        ) : (
          profile && (
            <Card>
              <CardContent>
                <Stack spacing={3}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                  >
                    <Typography variant="h6">
                      {t(translations.family_profile.user_details)}
                    </Typography>
                    {isSelectedSenior && <RemoveRelationship seniorId={seniorId} />}
                  </Stack>
                  <Stack direction="row" spacing={3} alignItems="center">
                    <Avatar
                      src={profile.picture}
                      alt={profile.displayName || profile.name}
                      sx={{width: 96, height: 96}}
                    />
                    <Typography variant="h6">{profile.displayName || profile.name}</Typography>
                  </Stack>
                  <Divider />
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {xs: '1fr', md: 'repeat(2, minmax(0, 1fr))'},
                      gap: 3,
                    }}
                  >
                    {fields.map(([label, value]) => (
                      <FlatInput
                        key={label}
                        label={t(label || '')}
                        value={value || '—'}
                        readonly
                        multiline={label === translations.user_form.address_input}
                        sx={
                          label === translations.user_form.address_input
                            ? {gridColumn: {md: '1 / -1'}}
                            : undefined
                        }
                      />
                    ))}
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          )
        )}
        {isSelectedSenior && (
          <InstitutionsSection
            key={seniorId}
            affiliations={affiliations}
            isLoading={affiliationsLoading}
          />
        )}
        <ContactsSection
          seniorId={seniorId}
          direction={RelationshipDirection.CaregiverToSenior}
          title={translations.family_profile.contacts}
          empty={translations.family_profile.empty}
          action={isSelectedSenior && profile ? <InviteCaregiver senior={profile} /> : undefined}
        />
        <ContactsSection
          seniorId={seniorId}
          direction={RelationshipDirection.SeniorToSenior}
          title={translations.family_profile.senior_contacts}
          empty={translations.family_profile.senior_empty}
        />
      </Stack>
    </ContentPage>
  );
}

function InstitutionsSection({
  affiliations,
  isLoading,
}: {
  affiliations?: AffiliationInfo[];
  isLoading: boolean;
}) {
  const {t} = useTranslation();
  const [selectedAffiliationId, setSelectedAffiliationId] = useState<string>();
  const selectedAffiliation = isLoading
    ? undefined
    : affiliations?.find((affiliation) => affiliation.id === selectedAffiliationId);
  const selectedInstitution = selectedAffiliation?.entity;
  const contactFields = selectedInstitution
    ? [
        {label: translations.entity_form.email_input, value: selectedInstitution.email},
        {
          label: translations.entity_form.landline_phone_number_input,
          value: selectedInstitution.landlinePhoneNumber,
        },
        {
          label: translations.entity_form.mobile_phone_number_input,
          value: selectedInstitution.mobilePhoneNumber,
        },
      ]
    : [];

  useLayoutEffect(() => {
    if (!selectedAffiliation) setSelectedAffiliationId(undefined);
  }, [selectedAffiliation]);

  return (
    <>
      <Card>
        <CardContent>
          <Typography variant="h6">{t(translations.family_profile.institutions)}</Typography>
          {isLoading ? (
            <LoadingContent />
          ) : affiliations?.length ? (
            <List
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 240px), 280px))',
                gap: 1,
                mt: 1,
              }}
            >
              {affiliations.map((affiliation) => (
                <ListItemButton
                  key={affiliation.id}
                  onClick={() => setSelectedAffiliationId(affiliation.id)}
                  sx={{border: 1, borderColor: 'divider', borderRadius: 2, minWidth: 0}}
                >
                  <ListItemAvatar>
                    <Avatar src={affiliation.entity.picture} alt={affiliation.entity.name}>
                      <BusinessCenterOutlinedIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={affiliation.entity.name} />
                </ListItemButton>
              ))}
            </List>
          ) : (
            <Typography color="text.secondary" sx={{mt: 1}}>
              {t(translations.family_services.none)}
            </Typography>
          )}
        </CardContent>
      </Card>
      <Dialog
        open={!!selectedInstitution}
        onClose={() => setSelectedAffiliationId(undefined)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{selectedInstitution?.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} alignItems="center">
            <Avatar
              src={selectedInstitution?.picture}
              alt={selectedInstitution?.name}
              sx={{width: 80, height: 80}}
            >
              <BusinessCenterOutlinedIcon />
            </Avatar>
            <Stack spacing={2} sx={{width: '100%'}}>
              {contactFields.map(({label, value}) => (
                <FlatInput key={label} label={t(label)} value={value?.trim() || '–'} readonly />
              ))}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedAffiliationId(undefined)}>
            {t(translations.family_profile.close)}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function ContactsSection({
  seniorId,
  direction,
  title,
  empty,
  action,
}: {
  seniorId: string;
  direction: RelationshipDirection;
  title: string;
  empty: string;
  action?: React.ReactNode;
}) {
  const {t} = useTranslation();
  const generation = useRef(0);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [contactError, setContactError] = useState<LoadError>();
  const [contactLoading, setContactLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [selected, setSelected] = useState<Contact>();
  const contactPending = useRef(false);

  const loadContacts = useCallback(
    async (offset: number) => {
      if (contactPending.current) return;
      contactPending.current = true;
      const requestGeneration = generation.current;
      setContactLoading(true);
      setContactError(undefined);
      try {
        const result = await UsersRepository.instance.getUserRelationships(
          seniorId,
          PAGE_SIZE,
          offset,
          {direction}
        );
        if (generation.current !== requestGeneration) return;
        const rows = result.data.map(({id, withUser, type}) => ({
          id,
          name: withUser.name,
          picture: withUser.picture,
          type,
        }));
        setContacts((previous) => (offset === 0 ? rows : [...previous, ...rows]));
        setSkip(offset + result.data.length);
        setTotal(result.totalCount);
      } catch (error) {
        if (generation.current === requestGeneration) setContactError(errorType(error));
      } finally {
        if (generation.current === requestGeneration) {
          contactPending.current = false;
          setContactLoading(false);
        }
      }
    },
    [seniorId, direction]
  );

  useLayoutEffect(() => {
    void loadContacts(0);
    return () => {
      generation.current += 1;
      contactPending.current = false;
    };
  }, [loadContacts]);

  const errorAlert = (error: LoadError, retry: () => void) => (
    <Alert
      severity="error"
      action={
        <Button color="inherit" size="small" onClick={retry}>
          {t(translations.family_profile.retry)}
        </Button>
      }
    >
      {t(translations.family_profile[error])}
    </Alert>
  );
  return (
    <>
      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap">
            <Typography variant="h6">{t(title)}</Typography>
            {action}
          </Stack>
          {contactLoading && contacts.length === 0 ? (
            <LoadingContent />
          ) : (
            <>
              {contacts.length > 0 && (
                <List
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 240px), 280px))',
                    gap: 1,
                  }}
                >
                  {contacts.map((contact) => (
                    <ListItemButton
                      key={contact.id}
                      onClick={() => setSelected(contact)}
                      sx={{border: 1, borderColor: 'divider', borderRadius: 2, minWidth: 0}}
                    >
                      <ListItemAvatar>
                        <Avatar src={contact.picture} alt={contact.name} />
                      </ListItemAvatar>
                      <ListItemText
                        primary={contact.name}
                        secondary={t(translations.relationship_types[contact.type])}
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
              {contactError ? (
                errorAlert(contactError, () => void loadContacts(skip))
              ) : contacts.length === 0 ? (
                <Typography color="text.secondary" sx={{mt: 2}}>
                  {t(empty)}
                </Typography>
              ) : null}
              {skip < total && !contactError && (
                <Button
                  disabled={contactLoading}
                  onClick={() => void loadContacts(skip)}
                  startIcon={contactLoading ? <CircularProgress size={16} /> : undefined}
                >
                  {t(
                    contactLoading
                      ? translations.family_profile.loading
                      : translations.family_profile.load_more
                  )}
                </Button>
              )}
            </>
          )}
        </CardContent>
      </Card>
      <Dialog open={!!selected} onClose={() => setSelected(undefined)} fullWidth maxWidth="xs">
        <DialogTitle>{selected?.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} alignItems="center">
            <Avatar src={selected?.picture} alt={selected?.name} sx={{width: 80, height: 80}} />
            <Typography>{selected && t(translations.relationship_types[selected.type])}</Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(undefined)}>
            {t(translations.family_profile.close)}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
